import React, { useState } from 'react';

import TitleOutlinedIcon from '@mui/icons-material/TitleOutlined';
import ViewCompactOutlinedIcon from '@mui/icons-material/ViewCompactOutlined';
import { List, ListItem, ListItemIcon, ListItemText, Switch, Typography } from '@mui/material';

import type { LensSpec, LensDisplay } from '~/types/leLens';

import { lensDisplayPreferences } from './lensDisplay';

type Preference = keyof LensDisplay;
type Props = {
  spec: LensSpec;
  disabled?: boolean;
  onChange: (preference: Preference, checked: boolean) => Promise<void>;
};

export default function LensDisplaySettings({ spec, disabled = false, onChange }: Props) {
  const [optimistic, setOptimistic] = useState<Partial<LensDisplay> | null>(null);
  const display = { ...lensDisplayPreferences(spec), ...optimistic };
  const change = async (preference: Preference, checked: boolean) => {
    if (disabled || optimistic) return;
    setOptimistic({ [preference]: checked });
    try {
      await onChange(preference, checked);
    } catch {
      // The shared mutation reports the error. Restore the saved switch state on failure.
    } finally {
      setOptimistic(null);
    }
  };
  const items = [
    {
      preference: 'compactLayout' as const,
      label: 'Compact layout',
      description: 'Keep period and filter controls in the menu.',
      checked: display.compactLayout !== false,
      icon: <ViewCompactOutlinedIcon fontSize="small" />,
    },
    {
      preference: 'showTitle' as const,
      label: 'Show title',
      description: 'Show the Lens name above the page.',
      checked: display.showTitle === true,
      icon: <TitleOutlinedIcon fontSize="small" />,
    },
  ];

  return (
    <>
      <Typography sx={{ fontSize: 15, fontWeight: 700, mb: 0.5 }} variant="subtitle2">
        Display
      </Typography>
      <List disablePadding sx={{ mx: -1 }}>
        {items.map((item) => (
          <ListItem key={item.preference} sx={{ borderRadius: 1.5, px: 1, py: 1.25 }}>
            <ListItemIcon sx={{ color: 'primary.main', minWidth: 38 }}>{item.icon}</ListItemIcon>
            <ListItemText
              primary={item.label}
              secondary={item.description}
              primaryTypographyProps={{ fontSize: 15, fontWeight: 600 }}
              secondaryTypographyProps={{ fontSize: 13, id: `lens-${item.preference}-help`, lineHeight: 1.45 }}
            />
            <Switch
              checked={item.checked}
              disabled={disabled || Boolean(optimistic)}
              onChange={(_event, checked) => void change(item.preference, checked)}
              size="small"
              slotProps={{ input: { 'aria-label': item.label, 'aria-describedby': `lens-${item.preference}-help` } }}
              sx={{ ml: 1 }}
            />
          </ListItem>
        ))}
      </List>
    </>
  );
}
